import React from "react";
import {
  expedienteBadgeStyle,
  expedienteBodyStyle,
  expedienteChevronBtnStyle,
  expedienteColors,
  expedienteEditBtnStyle,
  expedienteIconBoxStyle,
  expedienteMetaStyle,
  expedienteTitleBlockStyle,
  expedienteTitleStyle,
  getExpedienteActionsStyle,
  getExpedienteCardStyle,
  getExpedienteHeaderStyle,
  getExpedienteHeaderTopStyle,
  getExpedienteRailColumnStyle,
  getExpedienteRailDotStyle,
  getExpedienteRailLineStyle,
  getExpedienteRowOuterStyle,
} from "./expedienteTimelineStyles.ts";
import { useExpedienteNarrowLayout } from "./useExpedienteNarrowLayout.ts";

type ExpedienteDocumentRowProps = {
  title: string;
  obligatory?: boolean;
  showBadge?: boolean;
  badgeLabel?: string;
  metaText?: string | null;
  isMetaLoading?: boolean;
  hasFiles?: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onEdit?: () => void;
  expandedContent?: React.ReactNode;
  isLast?: boolean;
  animationDelay?: number;
  hideChevron?: boolean;
};

const ExpedienteDocumentRow = ({
  title,
  obligatory = false,
  showBadge = true,
  badgeLabel,
  metaText = null,
  isMetaLoading = false,
  hasFiles = false,
  expanded,
  onToggleExpand,
  onEdit,
  expandedContent,
  isLast = false,
  animationDelay = 0,
  hideChevron = false,
}: ExpedienteDocumentRowProps) => {
  const isNarrow = useExpedienteNarrowLayout();
  const accentColor = obligatory
    ? expedienteColors.accentObligatory
    : expedienteColors.accentOptional;

  const badgeText =
    badgeLabel ?? (obligatory ? "Obligatorio" : "Opcional");

  const badgeStyle =
    badgeLabel === "Notas"
      ? {
          ...expedienteBadgeStyle(false),
          backgroundColor: "#eef2ff",
          color: "#4338ca",
          border: "1px solid #c7d2fe",
        }
      : expedienteBadgeStyle(obligatory);

  const expandDisabled = isMetaLoading;

  const handleToggleExpand = () => {
    if (expandDisabled) return;
    onToggleExpand();
  };

  const renderMeta = () => {
    if (isMetaLoading) {
      return (
        <span style={expedienteMetaStyle}>
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
            style={{ width: 14, height: 14, borderWidth: 2 }}
          />
          Obteniendo...
        </span>
      );
    }
    if (metaText) {
      return <span style={expedienteMetaStyle}>{metaText}</span>;
    }
    if (!hasFiles && !isMetaLoading) {
      return (
        <span style={{ ...expedienteMetaStyle, fontStyle: "italic" }}>
          Sin archivos
        </span>
      );
    }
    return null;
  };

  return (
    <div
      style={{
        ...getExpedienteRowOuterStyle(),
        animation: `expedienteFadeIn 0.45s ease-out ${animationDelay}s forwards`,
        opacity: 0,
      }}
    >
      <div style={getExpedienteRailColumnStyle(isLast, accentColor)}>
        <div style={getExpedienteRailDotStyle(accentColor, hasFiles)} />
        {!isLast && <div style={getExpedienteRailLineStyle(isLast)} />}
      </div>

      <div
        style={{
          ...getExpedienteCardStyle(isNarrow, expanded && !expandDisabled),
          ...(expandDisabled ? { opacity: 0.88 } : {}),
        }}
      >
        <div
          style={{
            ...getExpedienteHeaderStyle(isNarrow),
            cursor: expandDisabled ? "default" : "pointer",
          }}
          onClick={handleToggleExpand}
          role={expandDisabled ? undefined : "button"}
          tabIndex={expandDisabled ? undefined : 0}
          aria-disabled={expandDisabled}
          onKeyDown={(e) => {
            if (expandDisabled) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleToggleExpand();
            }
          }}
        >
          <div style={getExpedienteHeaderTopStyle(isNarrow)}>
            {showBadge && <span style={badgeStyle}>{badgeText}</span>}
            <div style={expedienteIconBoxStyle}>
              <i className="fa-regular fa-file-lines" aria-hidden />
            </div>
            <div style={expedienteTitleBlockStyle}>
              <h4 style={expedienteTitleStyle}>{title}</h4>
              {isNarrow && renderMeta()}
            </div>
          </div>

          <div style={getExpedienteActionsStyle(isNarrow)}>
            {!isNarrow && renderMeta()}
            {!hideChevron && (
              <button
                type="button"
                style={{
                  ...expedienteChevronBtnStyle,
                  opacity: expandDisabled ? 0.45 : 1,
                  cursor: expandDisabled ? "not-allowed" : "pointer",
                }}
                aria-expanded={expanded}
                aria-label={
                  expandDisabled
                    ? "Cargando documento"
                    : expanded
                      ? "Contraer"
                      : "Expandir"
                }
                disabled={expandDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand();
                }}
              >
                <i
                  className={`fa ${
                    expanded ? "fa-chevron-up" : "fa-chevron-down"
                  }`}
                  style={{ fontSize: 12 }}
                />
              </button>
            )}
          </div>
        </div>

        {expanded && !expandDisabled && expandedContent && (
          <div style={expedienteBodyStyle}>{expandedContent}</div>
        )}
      </div>
    </div>
  );
};

export const ExpedienteDocumentExpandedBody = ({
  count,
  lastUpdated,
  formattedDate,
  onEdit,
}: {
  count: number;
  lastUpdated: string | null;
  formattedDate: (date: string) => string;
  onEdit: () => void;
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    }}
  >
    <div style={expedienteMetaStyle}>
      <span style={{ fontWeight: 600, color: expedienteColors.title }}>
        {count}
      </span>{" "}
      archivo{count !== 1 ? "s" : ""}
      {lastUpdated ? (
        <span> · {formattedDate(lastUpdated)}</span>
      ) : null}
    </div>
    <button type="button" style={expedienteEditBtnStyle} onClick={onEdit}>
      <i className="fa fa-edit" aria-hidden /> Editar
    </button>
  </div>
);

export default ExpedienteDocumentRow;
